import React, { useMemo, useState, useCallback } from 'react';
import { IconButton, Modal, Box } from '@mui/material';
import ShareFormWrapper from 'components/ShareForm/ShareFormWrapper';
import Share from 'components/Icons/Share';
import { Player } from 'components/CamPlayerKbygRewinds/usePlayerState/usePlayerState';
import { RefState } from 'hooks/useRefState';
import { trackEvent } from '@surfline/web-common';
import styles from './ShareModal.module.scss';

interface ShareModalProps {
  spotName: string;
  spotId: string;
  cameraId: string;
  cameraTitle: string;
  onOpenModal: () => void;
  onCloseModal: () => void;
  currentTimestampRef: RefState<Date | undefined>;
  spotTimezone: string;
  isRewindPlayer: boolean;
  currentPlayer?: Player;
  clipIndex?: number;
}

const ShareModal: React.FC<ShareModalProps> = ({
  spotName,
  spotId,
  cameraId,
  cameraTitle,
  onOpenModal,
  onCloseModal,
  currentTimestampRef,
  spotTimezone,
  isRewindPlayer,
  currentPlayer,
  clipIndex,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentShareUrl, setCurrentShareUrl] = useState<string>('');
  const [hasTrackedShareEvent, setHasTrackedShareEvent] = useState(false);

  const handleShareIconClick = useCallback(() => {
    setIsModalOpen(true);
    setHasTrackedShareEvent(false);
    onOpenModal();
  }, [onOpenModal]);

  const title = useMemo(() => {
    if (currentPlayer === Player.REWIND) {
      return 'Rewind';
    } else if (currentPlayer === Player.LIVE) {
      return 'Live Cam';
    } else {
      return 'Cam Highlights';
    }
  }, [currentPlayer]);

  const handleShareUrlChange = useCallback(
    (url: string) => {
      setCurrentShareUrl(url);

      // Only track the first time we get a URL after the modal is opened
      if (url && !hasTrackedShareEvent) {
        setHasTrackedShareEvent(true);
        trackEvent('Clicked Share Icon', {
          destinationUrl: url,
          locationCategory: 'Spot Page',
          shareChannel: 'Web',
          title,
          cameraId,
          contentType: isRewindPlayer ? 'Rewind' : 'Live Cam',
          location: 'Cam Player',
          sharedIconType: 'Initial',
          scrubberTimestamp: currentTimestampRef.current?.value?.toISOString(),
        });
      }
    },
    [isRewindPlayer, cameraId, currentTimestampRef, hasTrackedShareEvent],
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    onCloseModal();
  }, [onCloseModal]);

  return (
    <>
      <IconButton
        onClick={handleShareIconClick}
        className={styles.shareButton}
        disabled={false}
        data-testid="share-modal-button"
      >
        <Share />
      </IconButton>
      <Modal open={isModalOpen} className={styles.modal}>
        <Box className={styles.modalContent}>
          <ShareFormWrapper
            spotName={spotName}
            handleCloseClick={handleCloseModal}
            spotId={spotId}
            camId={cameraId}
            clipIndex={clipIndex}
            currentTimestampRef={currentTimestampRef}
            spotTimezone={spotTimezone}
            isRewindPlayer={isRewindPlayer}
            currentPlayer={currentPlayer}
            onShareUrlChange={handleShareUrlChange}
            cameraTitle={cameraTitle}
          />
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(ShareModal);
